/**
 * The keyboard contract every section honours (agora#221): one map, not a
 * separate one per section, and one place listing it. `AgoraShell` wires the
 * two shortcuts that are not any one section's own -- `?` opens
 * `ShortcutsDialog`, `g` then a letter switches section -- and `useListNav`
 * is the one small hook Mail's and Chat's own lists (agora#27) share for
 * `j`/`k`, rather than each keeping its own copy.
 *
 * Every shortcut here follows the same rules: it never fires while focus is
 * in an `<input>`, a `<textarea>`, a `<select>` or anything `contenteditable`
 * (`isTypingTarget`); never while a modifier is held, except `?` itself --
 * `Shift+/` -- where `shiftKey` is not a modifier to guard against; and
 * never while a dialog covers the page (`isAnyModalOpen`) -- the refuter on
 * c5c7b23 caught `j`/`k` and the `g` chords still acting on the page behind
 * `ShortcutsDialog` itself.
 *
 * `j`/`k` open the next or previous item outright (Gmail's own model), not
 * a separate cursor: there is nothing for `Enter` to do, so it is gone, and
 * a row's existing "on" class keeps meaning exactly what it always meant --
 * this is the open item -- with nothing new to style.
 */
import { Kbd, Modal } from '@wtfalch/design';
import { useEffect, useRef, useState } from 'react';
import { pathForSection, type Section } from './routes.ts';

/** Whether `el` is somewhere a key is meant to type rather than command. */
export function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  if (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  ) {
    return true;
  }
  return el instanceof HTMLElement && el.isContentEditable;
}

/** Whether any `Modal` (`@wtfalch/design`'s own -- `ShortcutsDialog`,
 *  `Compose`, `UploadDialog`, every one of them) is open over the page right
 *  now. `Modal` sets `aria-modal="true"` on its own dialog element itself
 *  (imperatively, once mounted), so this is the one query that catches all
 *  of them without any of them having to say so. */
function isAnyModalOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') !== null;
}

type ShortcutEntry = { keys: string; description: string };
type ShortcutGroup = { title: string; entries: ShortcutEntry[] };

/** The one list `ShortcutsDialog` draws -- every key this app answers to,
 *  wherever it lives. A shortcut is added here the day it ships. */
export const SHORTCUT_GROUPS: readonly ShortcutGroup[] = [
  {
    title: 'Go to',
    entries: [
      { keys: 'g then t', description: 'Today' },
      { keys: 'g then m', description: 'Mail' },
      { keys: 'g then c', description: 'Chat' },
      { keys: 'g then d', description: 'Calendar' },
    ],
  },
  {
    title: 'Search',
    entries: [
      { keys: '⌘K / Ctrl+K', description: 'Focus search, from anywhere' },
      { keys: '/', description: 'Focus search, outside a text field' },
    ],
  },
  {
    title: 'Mail and Chat lists',
    entries: [
      { keys: 'j', description: 'Open the next item' },
      { keys: 'k', description: 'Open the previous item' },
    ],
  },
  {
    title: 'Calendar',
    entries: [
      { keys: 'j', description: 'Next week' },
      { keys: 'k', description: 'Previous week' },
    ],
  },
  {
    title: 'Help',
    entries: [{ keys: '?', description: 'Show this dialog' }],
  },
];

const isListKey = (entry: { keys: string }) => entry.keys === 'j' || entry.keys === 'k';

/** Every shortcut, listed once, in a `Modal` (`@wtfalch/design`'s own) so it
 *  never needs a stylesheet of its own -- a handful of inline styles, the
 *  same rows `SearchField`'s own `<Kbd>` already draws. */
export function ShortcutsDialog({
  onClose,
  phone = false,
}: {
  onClose: () => void;
  /** A phone has no `j` and `k` to press, so the lists that are only those keys are left out. */
  phone?: boolean;
}) {
  const groups = phone
    ? SHORTCUT_GROUPS.filter((g) => !g.entries.every(isListKey))
    : SHORTCUT_GROUPS;
  return (
    // `dialog` is the package's own "a question, not a workspace" box: 440px,
    // and centred where the plain `.modal` is pinned near the top.
    <Modal title="Keyboard shortcuts" className="dialog" onClose={onClose}>
      {groups.map((group) => (
        <div key={group.title} style={{ marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>{group.title}</h3>
          {group.entries.map((entry) => (
            <div
              key={entry.keys}
              style={{
                display: 'grid',
                gridTemplateColumns: '9rem 1fr',
                alignItems: 'center',
                gap: 12,
                marginBottom: 6,
              }}
            >
              <span style={{ justifySelf: 'start' }}>
                <Kbd>{entry.keys}</Kbd>
              </span>
              <span>{entry.description}</span>
            </div>
          ))}
        </div>
      ))}
    </Modal>
  );
}

const SECTION_BY_KEY: Record<string, Section> = {
  t: 'today',
  m: 'mail',
  c: 'chat',
  d: 'calendar',
};

/** `?` opens the shortcuts dialog; `g` then `t`/`m`/`c`/`d` switches section
 *  (agora#221) -- the two shortcuts that belong to the shell rather than any
 *  one section, wired once here for every place `AgoraShell` wraps. `g`'s
 *  second key has one second to land before it is forgotten. */
export function useGlobalShortcuts({
  go,
  agoraId,
  chatAvailable,
}: {
  go: (path: string) => void;
  agoraId: string;
  chatAvailable: boolean;
}): {
  dialogOpen: boolean;
  openDialog: (opener?: HTMLElement | null) => void;
  closeDialog: () => void;
} {
  const [dialogOpen, setDialogOpen] = useState(false);
  // What to focus once the dialog closes. `Modal` puts focus back on what
  // had it, but a menu item is gone by then, and focus would fall to <body>.
  const openerRef = useRef<HTMLElement | null>(null);
  const goRef = useRef(go);
  goRef.current = go;
  const agoraIdRef = useRef(agoraId);
  agoraIdRef.current = agoraId;
  const chatAvailableRef = useRef(chatAvailable);
  chatAvailableRef.current = chatAvailable;
  // The effect below mounts its listener once (`[]`): a ref, kept current on
  // every render rather than an effect dependency, is how its closure still
  // sees "the dialog is open right now" without re-registering the
  // listener on every open/close.
  const dialogOpenRef = useRef(dialogOpen);
  dialogOpenRef.current = dialogOpen;

  useEffect(() => {
    let pendingG = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    function clearPending() {
      pendingG = false;
      clearTimeout(timer);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (dialogOpenRef.current || isAnyModalOpen()) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(document.activeElement)) return;

      if (pendingG) {
        const section = SECTION_BY_KEY[event.key];
        clearPending();
        if (!section) return;
        if (section === 'chat' && !chatAvailableRef.current) return;
        event.preventDefault();
        goRef.current(pathForSection(agoraIdRef.current, section));
        return;
      }
      if (event.key === '?') {
        event.preventDefault();
        openerRef.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setDialogOpen(true);
      } else if (event.key === 'g') {
        pendingG = true;
        timer = setTimeout(clearPending, 1000);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearPending();
    };
  }, []);

  return {
    dialogOpen,
    openDialog: (opener) => {
      openerRef.current = opener ?? null;
      setDialogOpen(true);
    },
    closeDialog: () => {
      setDialogOpen(false);
      // After `Modal` has unmounted and tried its own restore.
      setTimeout(() => {
        const opener = openerRef.current;
        if (opener?.isConnected && document.activeElement === document.body) opener.focus();
      }, 0);
    },
  };
}

/** Mail's and Chat's shared list navigation (agora#221, generalising
 *  agora#27): `j` opens the item after whichever is open, `k` the one
 *  before it (no arrow aliases: the arrow keys scroll the reader and move a
 *  focused select) -- Gmail's own model, not a separate
 *  cursor, so there is nothing for a row to draw that its existing
 *  `selected` prop was not drawing already. With nothing open, `j` opens
 *  the first item; `k` then has no "previous" and does nothing. `ids` is
 *  read through a ref at keypress time, not an effect dependency, so a
 *  fresh array every render (sorting, filtering) never matters -- the
 *  listener itself is mounted once. */
export function useListNav(
  ids: readonly string[],
  selectedId: string | null,
  onOpen: (id: string) => void,
): void {
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (isAnyModalOpen()) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (isTypingTarget(document.activeElement)) return;

      const ids = idsRef.current;
      if (ids.length === 0) return;
      const selectedId = selectedIdRef.current;
      const i = selectedId !== null ? ids.indexOf(selectedId) : -1;

      if (event.key === 'j') {
        event.preventDefault();
        const next = ids[i === -1 ? 0 : Math.min(i + 1, ids.length - 1)];
        if (next !== undefined) onOpenRef.current(next);
      } else if (event.key === 'k') {
        if (i <= 0) return;
        event.preventDefault();
        const prev = ids[i - 1];
        if (prev !== undefined) onOpenRef.current(prev);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}

/** Calendar's own j/k (agora#221): the same keys Mail's and Chat's lists
 *  answer to (`useListNav`), stepping between weeks instead of between rows
 *  -- a week is Calendar's "next/previous", the same shape a row is for a
 *  list. No `ArrowUp`/`ArrowDown` alias, same as `useListNav`: they mean
 *  "move within this view" and are left to the page.
 *
 *  `enabled` is read through a ref at keypress time rather than gating
 *  whether the listener is mounted, the same "one listener, not one per
 *  state" shape every hook in this file follows -- `Calendar.tsx` calls this
 *  once, unconditionally, and disables it while `Event` or `FindTime` are
 *  drawn instead of `Week`. */
export function useWeekNav({
  enabled,
  onPrev,
  onNext,
}: {
  enabled: boolean;
  onPrev: () => void;
  onNext: () => void;
}): void {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const onPrevRef = useRef(onPrev);
  onPrevRef.current = onPrev;
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!enabledRef.current) return;
      if (isAnyModalOpen()) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (isTypingTarget(document.activeElement)) return;

      if (event.key === 'j') {
        event.preventDefault();
        onNextRef.current();
      } else if (event.key === 'k') {
        event.preventDefault();
        onPrevRef.current();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
